import { useCallback, useEffect, useRef } from 'react';

import { REMINDER_CHANNELS } from '@src/shared/types/reminder';

/**
 * Plain text for SMS/WhatsApp (and similar) previews.
 * Preserves line breaks from textarea input or HTML (<br>, </p>).
 */
export function messageContentToPreviewText(content?: string): string {
  if (!content?.trim()) return '';
  return content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function isEmailChannel(channel: string) {
  return channel === REMINDER_CHANNELS.email;
}

function isPlainChannel(channel: string) {
  return (
    channel === REMINDER_CHANNELS.sms ||
    channel === REMINDER_CHANNELS.whatsapp
  );
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Turns textarea plain text into HTML so TipTap keeps line breaks and spaces. */
export function plainTextToEmailHtml(plain: string): string {
  if (!plain) return '';

  return plain
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => {
      if (line === '') return '<p><br></p>';
      return `<p style="white-space: pre-wrap">${escapeHtml(line)}</p>`;
    })
    .join('');
}

function looksLikeHtml(content: string) {
  return /<[a-z][\s\S]*>/i.test(content);
}

/**
 * Email HTML is drafted when switching to SMS/WhatsApp.
 * Switching back restores HTML only if the plain textarea was not edited.
 * Plain edits update the email draft so all channels stay in sync.
 */
export function useTemplateChannelContentDrafts(
  templateId?: number,
  initialChannel?: string,
  initialContent?: string
) {
  const emailHtmlDraftRef = useRef<string | null>(null);
  /** Plain text right after leaving email; used to detect textarea edits. */
  const plainSnapshotRef = useRef<string | null>(null);

  useEffect(() => {
    emailHtmlDraftRef.current = null;
    plainSnapshotRef.current = null;

    if (initialContent && isEmailChannel(initialChannel ?? '')) {
      emailHtmlDraftRef.current = initialContent;
    }
  }, [templateId, initialChannel, initialContent]);

  const resolveContentForChannelChange = useCallback(
    (previousChannel: string, nextChannel: string, currentContent: string) => {
      if (isEmailChannel(previousChannel) && isPlainChannel(nextChannel)) {
        emailHtmlDraftRef.current = currentContent;
        const plain = messageContentToPreviewText(currentContent);
        plainSnapshotRef.current = plain;
        return plain;
      }

      if (isPlainChannel(previousChannel) && isEmailChannel(nextChannel)) {
        const plainUnchanged =
          plainSnapshotRef.current !== null &&
          currentContent === plainSnapshotRef.current;

        if (plainUnchanged && emailHtmlDraftRef.current) {
          const draft = emailHtmlDraftRef.current;
          return looksLikeHtml(draft) ? draft : plainTextToEmailHtml(draft);
        }

        const html = plainTextToEmailHtml(currentContent);
        emailHtmlDraftRef.current = html;
        plainSnapshotRef.current = currentContent;
        return html;
      }

      if (isEmailChannel(previousChannel) && isEmailChannel(nextChannel)) {
        emailHtmlDraftRef.current = currentContent;
      }

      return currentContent;
    },
    []
  );

  return { resolveContentForChannelChange };
}
